import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
// routes
import { useRouter } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';
import { paths } from 'src/routes/paths';
// config
import { PATH_AFTER_LOGIN } from 'src/config-global';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// auth
import { useAuthContext } from 'src/auth/hooks';
// components
import Iconify from 'src/components/iconify';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { BackendValidationError } from 'src/utils/backend-validation-error';
import { INTERNAL_SERVER_ERROR, UNKNOWN_ERROR } from 'src/utils/axios';
//
import { Box } from '@mui/material';
import GoogleButton from './google-button';

// ----------------------------------------------------------------------

export default function RegisterForm() {
  const { register } = useAuthContext();

  const router = useRouter();

  const [errorMsgs, setErrorMsgs] = useState<BackendValidationError[]>([]);

  const password = useBoolean();

  const RegisterSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    organizationName: Yup.string().required('Company name is required'),
    phone: Yup.string().required('Phone is required'),
    email: Yup.string().required('Email is required').email('Email must be a valid email address'),
    password: Yup.string()
      .required('Password is required')
      .min(8, 'Password must be at least 8 characters'),
  });

  const methods = useForm({
    resolver: yupResolver(RegisterSchema),
    defaultValues: { name: '', organizationName: '', phone: '', email: '', password: '' },
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      await register?.(data.name, data.phone, data.email, data.password, data.organizationName);
      router.push(PATH_AFTER_LOGIN);
    } catch (error) {
      setErrorMsgs(
        error?.response?.data || [
          { message: error?.response?.status === 500 ? INTERNAL_SERVER_ERROR : UNKNOWN_ERROR },
        ]
      );
    }
  });

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Stack spacing={2.5}>
        <Stack spacing={0.75}>
          <Box sx={{ justifyContent: 'center', display: 'flex' }}>
            <Box
              component="img"
              src="/logo/logo_ifta.png"
              alt="Counting Trucks IFTA"
              data-testid="ifta-logo"
              sx={{ width: 230, height: 'auto' }}
            />
          </Box>

          <Typography
            variant="h3"
            component="h1"
            sx={{
              pt: 3,
              fontFamily: "'Barlow', 'Public Sans', sans-serif",
              fontWeight: 900,
              letterSpacing: '-0.01em',
            }}
          >
            Create your IFTA account
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            File your first quarter in minutes.
          </Typography>
        </Stack>

        {errorMsgs.map((errorMsg, index) => (
          <Alert key={index} severity="error">
            {errorMsg.message}
          </Alert>
        ))}

        <RHFTextField name="name" label="Your name" />
        <RHFTextField name="organizationName" label="Company name" />
        <RHFTextField name="phone" label="Phone" />
        <RHFTextField name="email" label="Email address" />

        <RHFTextField
          name="password"
          label="Password"
          type={password.value ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={password.onToggle} edge="end">
                  <Iconify icon={password.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <LoadingButton
          fullWidth
          color="secondary"
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
        >
          Create account
        </LoadingButton>

        <Divider>or</Divider>

        <GoogleButton label="Sign up with Google" />

        <Typography variant="body2" sx={{ textAlign: 'center' }}>
          Already have an account?{' '}
          <Link
            component={RouterLink}
            href={paths.auth.login}
            variant="subtitle2"
            sx={{
              color: 'text.primary',
              textDecorationColor: 'inherit',
              textDecoration: 'underline',
            }}
          >
            Sign in
          </Link>
        </Typography>
      </Stack>
    </FormProvider>
  );
}
